import { z } from 'zod';
import {
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  EvaluationInferenceProviderIdSchema,
  EvaluationRunKindSchema,
  EvaluationRunStatusSchema,
} from './evaluation-run-shared.schemas';

export const EvaluationRunScenarioInfoSchema = z.object({
  name: z.string().nullable(),
  scenarioId: z.string().nullable(),
  category: z.string().nullable(),
  target: z.string().nullable(),
  signalId: z.string().nullable(),
  passThreshold: z.number().nullable(),
  source: z.string().nullable(),
  branch: z.string().nullable(),
  repoUrl: z.string().nullable(),
  scenarioPath: z.string().nullable(),
});

export const EngineSummaryMetricsSchema = z.object({
  endpoint_match: z.boolean().optional(),
  parameter_match: z.boolean().optional(),
  trace_export_present: z.boolean().optional(),
  execution_status: z.string().optional(),
  evidence_clarity: z.string().optional(),
  steps_executed: z.number().optional(),
  steps_with_results: z.number().optional(),
  payload_variety: z.string().optional(),
  vulnerability_confirmation: z.string().optional(),
  specification_compliance: z.number().optional(),
  evidence_richness: z.number().optional(),
  post_attack_analysis_completeness: z.number().optional(),
  traceProvider: z.string().optional(),
  traceId: z.string().optional(),
  evaluator: z.string().optional(),
  traceExportAvailable: z.boolean().optional(),
  traceExportObservationCount: z.number().optional(),
  passed: z.boolean().optional(),
  reason: z.string().optional(),
});

export const EngineSummaryIterationSchema = z.object({
  iteration: z.number(),
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()),
  score: z.number(),
  status: z.string(),
  error: z.string().nullable(),
  metrics: EngineSummaryMetricsSchema,
});

export type EvaluationRunScenarioInfo = z.infer<
  typeof EvaluationRunScenarioInfoSchema
>;
export type EvaluationEngineSummaryMetrics = z.infer<
  typeof EngineSummaryMetricsSchema
>;
export type EvaluationEngineSummaryIteration = z.infer<
  typeof EngineSummaryIterationSchema
>;

export const EvaluationRunListItemSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  tenantId: z.string().nullable(),
  userId: z.string().nullable(),
  kind: EvaluationRunKindSchema,
  status: EvaluationRunStatusSchema,
  title: z.string().nullable(),
  queuedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  failedReason: z.string().nullable(),
  parentRunId: z.string().nullable(),
  traceId: z.string().nullable().optional(),
  executionState: z
    .enum(['TRIGGERED_WAITING', 'RESUMED_EVALUATING', 'EVALUATING'])
    .nullable()
    .optional(),
  executionStatus: z.string().nullable().optional(),
  score: z.number().nullable(),
  passed: z.boolean().nullable(),
  reason: z.string().nullable(),
  summary: z.string().nullable(),
  triggerType: z.string().nullable(),
  scenarioInfo: EvaluationRunScenarioInfoSchema.nullable(),
  componentId: z.string().nullable().optional(),
  experimentId: z.string().nullable().optional(),
  experimentName: z.string().nullable().optional(),
  experimentConfigName: z.string().nullable().optional(),
  appStateId: z.string().nullable().optional(),
  matrixId: z.string().nullable().optional(),
  variantName: z.string().nullable().optional(),
  variantEngine: z.string().nullable().optional(),
  providerId: EvaluationInferenceProviderIdSchema.nullable().optional(),
  durationMs: z.number().nullable().optional(),
  execDurationMs: z.number().nullable().optional(),
  queueMs: z.number().nullable().optional(),
  costUsd: z.number().nullable().optional(),
  expectedOutcome: z.string().nullable().optional(),
  actualOutcome: z.string().nullable().optional(),
  outcomeMatch: z.boolean().nullable().optional(),
  outcomeDescriptorId: z.string().nullable().optional(),
  evaluationMode: z.string().nullable().optional(),
  requestedModel: z.string().nullable().optional(),
  requestedTemperature: z.number().nullable().optional(),
  actualModel: z.string().nullable().optional(),
  actualProvider: z.string().nullable().optional(),
  repeatIndex: z.number().nullable().optional(),
  signalTitle: z.string().nullable().optional(),
  signalDescription: z.string().nullable().optional(),
  signalCategory: z.string().nullable().optional(),
  postAttackTraceId: z.string().nullable().optional(),
  blockReason: z.string().nullable().optional(),
  skipAttackReasonType: z.string().nullable().optional(),
  skipAttackReason: z.string().nullable().optional(),
  postAttackConfidence: z.enum(['low', 'medium', 'high']).nullable().optional(),
  postAttackConfidenceReason: z.string().nullable().optional(),
});

export const EvaluationRunDetailSchema = EvaluationRunListItemSchema.extend({
  metadata: z.record(z.string(), z.unknown()).nullable(),
});

export type EvaluationRunListItem = z.infer<typeof EvaluationRunListItemSchema>;

export type EvaluationRunDetail = z.infer<typeof EvaluationRunDetailSchema>;

export const EvaluationRunOutcomeFilterSchema = z.enum([
  'match',
  'mismatch',
  'unlabelled',
]);

export const EvaluationRunIterationOutcomeSchema = z.object({
  descriptorId: z.string(),
  matcher: z.string().nullable(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
  match: z.boolean().nullable(),
});

export const EvaluationRunIterationSchema = z.object({
  iteration: z.number(),
  status: z.string(),
  score: z.number().nullable(),
  error: z.string().nullable(),
  expectedOutcome: z.string().nullable(),
  actualOutcome: z.string().nullable(),
  outcomeMatch: z.boolean().nullable(),
  outcomeDescriptorId: z.string().nullable(),
  outcomes: z.array(EvaluationRunIterationOutcomeSchema).default([]),
  costUsd: z.number().nullable(),
  reason: z.string().nullable(),
  traceId: z.string().nullable(),
  signalId: z.string().nullable(),
  postAttackTraceId: z.string().nullable().optional(),
  postAttackConfidence: z.enum(['low', 'medium', 'high']).nullable().optional(),
});

export const EvaluationRunIterationsSchema = z.object({
  iterations: z.array(EvaluationRunIterationSchema),
});

export type EvaluationRunIteration = z.infer<
  typeof EvaluationRunIterationSchema
>;

export type EvaluationRunIterationOutcome = z.infer<
  typeof EvaluationRunIterationOutcomeSchema
>;

export type EvaluationRunOutcomeFilter = z.infer<
  typeof EvaluationRunOutcomeFilterSchema
>;

export const EvaluationRunLogsSchema = z.object({
  logs: z.array(
    z.object({
      sequence: z.number(),
      level: z.string().nullable(),
      chunk: z.string(),
      createdAt: z.iso.datetime(),
    }),
  ),
});

const ArtifactEngineSummarySchema = z.object({
  score: z.number().nullable(),
  summary: z.string().nullable(),
  iterations: z.array(EngineSummaryIterationSchema),
});

export const EvaluationRunArtifactsSchema = z.object({
  artifacts: z.array(
    z.object({
      id: z.string(),
      artifactType: z.string(),
      label: z.string().nullable(),
      uri: z.string().nullable(),
      mimeType: z.string().nullable(),
      sizeBytes: z.number().nullable(),
      createdAt: z.iso.datetime(),
      result: z.enum(['PASSED', 'FAILED', 'UNKNOWN']).nullable(),
      score: z.number().nullable(),
      status: z.string().nullable(),
      passed: z.boolean().nullable(),
      reason: z.string().nullable(),
      succeededIterations: z.number().nullable(),
      totalIterations: z.number().nullable(),
      failedIterations: z.number().nullable(),
      executionSummary: z.string().nullable(),
      metadata: z.record(z.string(), z.unknown()).nullable(),
      engineSummary: ArtifactEngineSummarySchema.nullable(),
    }),
  ),
});

export type EvaluationRunArtifact = z.infer<
  typeof EvaluationRunArtifactsSchema
>['artifacts'][number];

export const EvaluationRunArtifactContentSchema = z.object({
  content: z.string(),
  mimeType: z.string().nullable(),
  truncated: z.boolean(),
});

export const RerunEvaluationRunRequestSchema = z.object({
  reason: z.string().min(1),
  mode: z
    .enum(['REUSE_TRACE', 'TRIGGER_NEW_FROM_SIGNAL', 'RESUME_THREAD'])
    .optional(),
});

export const RerunEvaluationRunResponseSchema = z.object({
  acknowledged: z.boolean(),
});

export const CancelEvaluationRunResponseSchema = z.object({
  cancelled: z.boolean(),
  runId: z.string(),
  status: z.literal('CANCELLED'),
});

export const DeleteEvaluationRunResponseSchema = z.object({
  deleted: z.boolean(),
  runId: z.string(),
});

export const UploadEvaluationScenarioRequestSchema = z.object({
  fileName: z.string().min(1),
  content: z.string().min(1),
});

export const UploadEvaluationScenarioResponseSchema = z.object({
  uploadedScenarioId: z.string(),
  fileName: z.string(),
  path: z.string(),
});

export const ListEvaluationScenariosQuerySchema = z.object({
  branch: z.string().optional(),
  repoUrl: z.url().optional(),
  revision: z.string().optional(),
});

export const EvaluationScenarioListItemSchema = z.object({
  path: z.string(),
  source: z.enum(['local', 'git']),
  revision: z.string().optional(),
});

export const ListEvaluationScenariosResponseSchema = z.object({
  scenarios: z.array(EvaluationScenarioListItemSchema),
});

export const EVALUATION_SIGNALS_LIST_MAX_LIMIT = 500;

export const ListEvaluationSignalsQuerySchema = z.object({
  applicationId: z.string().min(1),
  tenantId: z.string().min(1).optional(),
  limit: z.coerce
    .number()
    .min(1)
    .max(EVALUATION_SIGNALS_LIST_MAX_LIMIT)
    .optional()
    .prefault(200),
  offset: z.coerce.number().int().min(0).optional().prefault(0),
});

export const EvaluationSignalListItemSchema = z.object({
  id: z.string(),
  stage: SignalStageSchema,
  status: SignalStatusSchema.nullish().transform((value) => value ?? null),
  category: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  analysis: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  endpointUrl: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
});

export const ListEvaluationSignalsResponseSchema = z.object({
  signals: z.array(EvaluationSignalListItemSchema),
});
